import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// * Utils
import {
  getGroupedOptions,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import {
  actionValidation,
  filterOptionGroupsForEdit,
  isResidualNode,
  mapUnitsToDataStreams,
  validateTree,
} from '../../utils/energyTreeFunctions';

// * MUI Icons
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// * API
import { usePutEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';

const EditNodeTab = ({
  clickedNode,
  decoratedUnits,
  localData,
  isMainMeter,
  handleClose,
}) => {
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [putEnergyTreeNodes] = usePutEnergyTreeNodesMutation();

  const [formState, setFormState] = useState({
    energyGroupId: clickedNode?.energyGroupId || null,
    name: clickedNode?.name || '',
    type: clickedNode?.type || '',
    energyTreeNodeId: clickedNode?.energyTreeNodeId || null,
    parentId: clickedNode?.parentId || null,
    dataStreams: clickedNode?.dataStreams || [],
    options: clickedNode?.options || null,
  });

  const [residualSource, setResidualSource] = useState(
    formState?.options?.specialNodeType || null,
  ); // default

  const handleUpdateNode = async () => {
    try {
      await putEnergyTreeNodes({
        accountId: account.AccountId,
        energyTreeNodeId: clickedNode.energyTreeNodeId,
        updatedEnergyTreeNode: formState,
      }).unwrap();
      dispatch(
        showSnackbar({ message: 'Node opdateret', severity: 'success' }),
      );
      handleClose();
    } catch (error) {
      console.error('Failed to update node:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdatering af node',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const allOptionGroups = useMemo(() => getGroupedOptions(), []);
  const filteredOptionGroups = useMemo(() => {
    if (!clickedNode) return allOptionGroups;
    return filterOptionGroupsForEdit(
      clickedNode,
      localData,
      allOptionGroups,
      formState.dataStreams,
    ); // ✅
  }, [clickedNode, allOptionGroups, localData, formState.dataStreams]);

  const validation = useMemo(() => {
    return actionValidation({
      action: 'edit',
      targetType: formState.type,
      dataStreams: formState.dataStreams,
      parent: clickedNode,
      currentNode: clickedNode,
      tree: localData,
    });
  }, [formState.type, formState.dataStreams, clickedNode, localData]);

  return (
    <Stack spacing={2}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography variant="subtitle1" color="text.primary" fontWeight={600}>
          {clickedNode?.name || 'Node Details'}
        </Typography>
        <Box
          style={{
            backgroundColor: getTypeColor(clickedNode?.type),
            position: 'relative', // Changed from 'absolute' to maintain layout
            borderRadius: '25px',
            width: '40px',
            height: '40px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid divider`,
          }}
        >
          <i
            style={{
              color: 'white',
            }}
            className={`fa-solid ${getTypeIcon(clickedNode?.type)}`}
          ></i>
        </Box>
      </Box>

      {/* {validation.isValid === false && (
        <Alert
          severity="info"
          sx={(theme) => ({
            mt: 1,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            borderLeft: `6px solid ${theme.palette.info.main}`,
          })}
        >
          <AlertTitle> Rediger node</AlertTitle>
          {validation.invalidReason || 'Ugyldig konfiguration'}
        </Alert>
      )} */}

      <>
        <TextField
          label="Navn"
          value={formState.name}
          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
          helperText="Ret navnet på denne node."
          fullWidth
          variant="filled"
        />

        <TextField
          select
          label="Node Type"
          value={formState.type}
          onChange={(e) => setFormState({ ...formState, type: e.target.value })}
          helperText={
            validation.isValid
              ? 'Vælg en type for noden.'
              : validation.invalidReason || 'Understøttes ikke'
          }
          disabled={isMainMeter}
          fullWidth
          variant="filled"
          SelectProps={{
            MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <i className={`fa-solid ${getTypeIcon(formState.type)}`} />
                </InputAdornment>
              ),
            },
          }}
        >
          <MenuItem value="" disabled>
            <Typography color="text.secondary">Vælg type</Typography>
          </MenuItem>

          {Object.entries(filteredOptionGroups).map(([groupLabel, options]) => {
            if (!options.length) return null;

            return [
              <ListSubheader key={`header-${groupLabel}`}>
                {groupLabel}
              </ListSubheader>,
              ...options.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Box display="flex" width="100%">
                    <Box
                      sx={{
                        backgroundColor: getTypeColor(value),
                        color: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        mr: 1,
                        height: 28,
                        width: 28,
                        justifyContent: 'center',
                        fontSize: '14px',
                      }}
                    >
                      <i className={`fa-solid ${getTypeIcon(value)}`} />
                    </Box>
                    <Typography>{label}</Typography>
                  </Box>
                </MenuItem>
              )),
            ];
          })}
        </TextField>

        {decoratedUnits && !isResidualNode(clickedNode) && (
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={!validation.isValid}
            options={decoratedUnits}
            getOptionLabel={(option) => `${option.UnitId} - ${option.Name}`}
            value={formState.dataStreams.map((ds) => {
              return decoratedUnits.find((unit) => unit.UnitId === ds.unitId);
            })}
            inputValue={searchText}
            onInputChange={(event, newInputValue, reason) => {
              if (reason !== 'reset') {
                setSearchText(newInputValue);
              }
            }}
            getOptionDisabled={(option) =>
              !!option.usedInNode || !!option.notSupportedReason
            }
            onClose={() => setSearchText('')}
            onChange={(e, newValue) => {
              setFormState({
                ...formState,
                dataStreams: mapUnitsToDataStreams(newValue),
              });
            }}
            filterSelectedOptions
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'space-between'}
                  width={'100%'}
                >
                  <Typography>
                    ({option.UnitId}) {option.Name}
                  </Typography>
                  {option.usedInNode && (
                    <Typography variant="caption" color={'text.secondary'}>
                      Anvendt for: {option.usedInNode}
                    </Typography>
                  )}
                  {option.notSupportedReason && (
                    <Typography variant="caption" color={'error'}>
                      {option.notSupportedReason}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label="Vælg"
                placeholder=" Søg"
                disabled={validation.isUnitSelectionDisabled}
                helperText={
                  !validation.isUnitSelectionEnabled
                    ? validation.unitSelectionDisabledReason
                    : 'Vælg hvilke datakilder der skal knyttes til denne node.'
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.UnitId}
                  label={`${option.Name}`}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        )}

        {isResidualNode(clickedNode) && (
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <FormHelperText>
              Dette er en residual node. Det er ikke muligt at knytte
              datastrømme til denne node.
            </FormHelperText>
            <TextField
              select
              label="Rolle"
              sx={{ mt: 2 }}
              value={formState?.options?.specialNodeType || ''}
              helperText="Vælg hvilken rolle denne node har. Dette påvirker hvordan det repræsenterede data fremgår."
              onChange={(e) => {
                const next = e.target.value;
                setResidualSource(next);

                setFormState((fs) => {
                  const updated = { ...fs };
                  updated.options = {
                    ...updated.options,
                    specialNodeType: next,
                  };
                  if (next !== 'FlatResidual') {
                    updated.options.power = null;
                  }
                  if (next !== 'PercentageResidual') {
                    updated.options.percentage = null;
                  }
                  return updated;
                });
              }}
              fullWidth
              variant="filled"
            >
              <MenuItem value="RestResidual">Restforbrug</MenuItem>
              <MenuItem value="FlatResidual">Fast effekt (kW)</MenuItem>
              <MenuItem value="PercentageResidual">Procentandel (%)</MenuItem>
            </TextField>

            {residualSource === 'FlatResidual' && (
              <TextField
                label="Fast effekt (kW)"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                value={formState?.options?.power || ''}
                onChange={(e) => {
                  setFormState((fs) => ({
                    ...fs,
                    options: {
                      ...fs.options,
                      power:
                        e.target.value === '' ? null : Number(e.target.value),
                    },
                  }));
                }}
                variant="filled"
                fullWidth
                helperText="Angiv en fast residualeffekt i kW."
              />
            )}

            {residualSource === 'PercentageResidual' && (
              <Box sx={{ mt: 2 }}>
                <Typography gutterBottom>Procentandel (%)</Typography>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <TextField
                    type="number"
                    variant="filled"
                    hiddenLabel
                    value={(
                      (formState?.options?.percentage ?? 0) * 100
                    ).toFixed(1)} // limit to 2 decimals
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value);
                      const clamped = isNaN(pct)
                        ? 0
                        : Math.min(Math.max(pct, 0), 100);
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          percentage: clamped / 100,
                        },
                      }));
                    }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.1,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <PercentRoundedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Slider
                    value={formState?.options?.percentage * 100}
                    onChange={(_, newVal) =>
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          percentage: newVal / 100,
                        },
                      }))
                    }
                    min={0.1}
                    max={100}
                    step={0.1}
                  />
                </Box>
              </Box>
            )}
          </Card>
        )}
      </>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginTop: '16px',
        }}
      >
        <Button
          onClick={handleUpdateNode}
          disabled={!validation.isValid}
          variant="contained"
        >
          {t('tree_actions.save_changes')}
        </Button>
      </div>
    </Stack>
  );
};

export default EditNodeTab;
